/**
 * Performance-Aware Color Transformations
 * Integrates WebGL acceleration with memory optimization for optimal performance
 */

import type { CMYK } from '../colorEngine';
import type { ColorTransformMatrix } from '../../types/substrate';
import { memoryOptimizer } from './memoryOptimizer';
import { webglAccelerator } from './webglAccelerator';

export interface PerformanceConfig {
  /** Enable WebGL acceleration */
  enableWebGL: boolean;
  /** Minimum batch size for GPU processing */
  minBatchSize: number;
  /** Performance monitoring enabled */
  enableMonitoring: boolean;
  /** Auto-adjust based on performance metrics */
  autoAdjust: boolean;
}

export interface PerformanceReport {
  totalTransforms: number;
  gpuTransforms: number;
  cpuTransforms: number;
  averageGPUTime: number;
  averageCPUTime: number;
  memoryOptimizations: number;
  webglEnabled: boolean;
  recommendations: string[];
}

/**
 * Performance-aware color transformation manager
 */
export class PerformanceColorTransforms {
  private config: PerformanceConfig;
  private metrics: PerformanceReport;
  private isWebGLInitialized = false;

  constructor(config: Partial<PerformanceConfig> = {}) {
    this.config = {
      enableWebGL: true,
      minBatchSize: 50,
      enableMonitoring: true,
      autoAdjust: true,
      ...config
    };

    this.metrics = {
      totalTransforms: 0,
      gpuTransforms: 0,
      cpuTransforms: 0,
      averageGPUTime: 0,
      averageCPUTime: 0,
      memoryOptimizations: 0,
      webglEnabled: false,
      recommendations: []
    };

    this.initialize();
  }

  /**
   * Initialize performance systems
   */
  private initialize(): void {
    if (this.config.enableWebGL) {
      this.isWebGLInitialized = webglAccelerator.initialize();
      this.metrics.webglEnabled = this.isWebGLInitialized;
      
      if (this.isWebGLInitialized) {
        console.log('WebGL acceleration enabled for color transformations');
      } else {
        console.log('WebGL acceleration not available, using CPU fallback');
      }
    }

    if (this.config.enableMonitoring) {
      // Start memory monitoring
      memoryOptimizer.startMonitoring(5000);
    }
  }

  /**
   * Apply color transformation with optimal performance routing
   */
  applyColorTransform(cmyk: CMYK, transform: ColorTransformMatrix): CMYK {
    const startTime = performance.now();

    // Use memory-optimized CPU path for single transformations
    // (GPU overhead is too high for single operations)
    const result = this.applyColorTransformCPU(cmyk, transform);

    // Update metrics
    const endTime = performance.now();
    this.updateMetrics(1, false, endTime - startTime);

    return result;
  }

  /**
   * Batch process multiple color transformations with optimal routing
   */
  batchTransform(colors: CMYK[], transform: ColorTransformMatrix): CMYK[] {
    const startTime = performance.now();
    let result: CMYK[];
    let usedGPU = false;

    // Determine optimal processing method
    if (this.shouldUseGPU(colors.length)) {
      // Use GPU for large batches
      result = webglAccelerator.batchTransform(colors, transform);
      usedGPU = true;
    } else {
      // Use memory-optimized CPU for smaller batches
      result = colors.map(cmyk => this.applyColorTransformCPU(cmyk, transform));
      usedGPU = false;
    }

    // Update metrics
    const endTime = performance.now();
    this.updateMetrics(colors.length, usedGPU, endTime - startTime);

    return result;
  }

  /**
   * Memory-optimized CPU color transformation
   */
  private applyColorTransformCPU(cmyk: CMYK, transform: ColorTransformMatrix): CMYK {
    if (transform.inputSpace !== 'cmyk' || transform.outputSpace !== 'cmyk') {
      throw new Error('Transform must be from CMYK to CMYK for direct application');
    }

    // Get matrix pool for memory optimization
    const matrixPool = memoryOptimizer.getMatrixPool();
    const pooledMatrix = matrixPool.acquire();
    
    try {
      const input = [cmyk.c / 100, cmyk.m / 100, cmyk.y / 100, cmyk.k / 100, 1];
      const output = [0, 0, 0, 0];

      // Use pooled matrix for calculations
      for (let i = 0; i < 4; i++) {
        for (let j = 0; j < 5; j++) {
          output[i] += transform.matrix[i][j] * input[j];
        }
      }

      return {
        c: Math.max(0, Math.min(100, output[0] * 100)),
        m: Math.max(0, Math.min(100, output[1] * 100)),
        y: Math.max(0, Math.min(100, output[2] * 100)),
        k: Math.max(0, Math.min(100, output[3] * 100))
      };
    } finally {
      // Release pooled matrix back to pool
      matrixPool.release(pooledMatrix);
      this.metrics.memoryOptimizations++;
    }
  }

  /**
   * Determine if GPU should be used for current batch
   */
  private shouldUseGPU(batchSize: number): boolean {
    if (!this.config.enableWebGL || !this.isWebGLInitialized) {
      return false;
    }

    // Use configured minimum batch size
    if (batchSize >= this.config.minBatchSize) {
      return true;
    }

    // Auto-adjust based on performance metrics
    if (this.config.autoAdjust) {
      return webglAccelerator.shouldUseGPU(batchSize);
    }

    return false;
  }

  /**
   * Update performance metrics
   */
  private updateMetrics(count: number, usedGPU: boolean, timeMs: number): void {
    this.metrics.totalTransforms += count;

    if (usedGPU) {
      this.metrics.gpuTransforms += count;
      const totalGPUTime = this.metrics.averageGPUTime * (this.metrics.gpuTransforms - count) + timeMs;
      this.metrics.averageGPUTime = totalGPUTime / this.metrics.gpuTransforms;
    } else {
      this.metrics.cpuTransforms += count;
      const totalCPUTime = this.metrics.averageCPUTime * (this.metrics.cpuTransforms - count) + timeMs;
      this.metrics.averageCPUTime = totalCPUTime / this.metrics.cpuTransforms;
    }

    // Auto-adjust configuration based on performance
    if (this.config.autoAdjust && this.metrics.totalTransforms % 100 === 0) {
      this.autoAdjustConfiguration();
    }
  }

  /**
   * Auto-adjust configuration based on performance metrics
   */
  private autoAdjustConfiguration(): void {
    const recommendations: string[] = [];

    // Adjust GPU threshold based on performance
    if (this.metrics.gpuTransforms > 0 && this.metrics.cpuTransforms > 0) {
      const gpuEfficiency = this.metrics.averageGPUTime / this.metrics.averageCPUTime;
      
      if (gpuEfficiency < 0.8) {
        // GPU is significantly faster, lower threshold
        this.config.minBatchSize = Math.max(10, this.config.minBatchSize - 10);
        recommendations.push(`GPU is ${(1 - gpuEfficiency) * 100}% faster, reduced batch threshold to ${this.config.minBatchSize}`);
      } else if (gpuEfficiency > 1.2) {
        // GPU is slower, raise threshold
        this.config.minBatchSize = Math.min(200, this.config.minBatchSize + 10);
        recommendations.push(`GPU is ${(gpuEfficiency - 1) * 100}% slower, increased batch threshold to ${this.config.minBatchSize}`);
      }
    }

    // Memory optimization recommendations
    const memoryReport = memoryOptimizer.getMemoryReport();
    if (memoryReport.usage.heapUsed > 100) {
      recommendations.push('High memory usage detected, consider reducing cache sizes');
    }

    this.metrics.recommendations = recommendations;
  }

  /**
   * Get comprehensive performance report
   */
  getPerformanceReport(): PerformanceReport {
    // Update recommendations
    if (this.config.autoAdjust) {
      this.autoAdjustConfiguration();
    }

    return { ...this.metrics };
  }

  /**
   * Get WebGL capabilities
   */
  getWebGLCapabilities() {
    return webglAccelerator.getCapabilities();
  }

  /**
   * Get memory optimization report
   */
  getMemoryReport() {
    return memoryOptimizer.getMemoryReport();
  }

  /**
   * Update configuration
   */
  updateConfig(newConfig: Partial<PerformanceConfig>): void {
    this.config = { ...this.config, ...newConfig };
    
    // Reinitialize if WebGL setting changed
    if (newConfig.enableWebGL !== undefined) {
      if (newConfig.enableWebGL && !this.isWebGLInitialized) {
        this.isWebGLInitialized = webglAccelerator.initialize();
        this.metrics.webglEnabled = this.isWebGLInitialized;
      } else if (!newConfig.enableWebGL && this.isWebGLInitialized) {
        webglAccelerator.dispose();
        this.isWebGLInitialized = false;
        this.metrics.webglEnabled = false;
      }
    }
  }

  /**
   * Get current configuration
   */
  getConfig(): PerformanceConfig {
    return { ...this.config };
  }

  /**
   * Reset all metrics
   */
  resetMetrics(): void {
    this.metrics = {
      totalTransforms: 0,
      gpuTransforms: 0,
      cpuTransforms: 0,
      averageGPUTime: 0,
      averageCPUTime: 0,
      memoryOptimizations: 0,
      webglEnabled: this.metrics.webglEnabled,
      recommendations: []
    };

    webglAccelerator.resetMetrics();
  }

  /**
   * Optimize memory usage
   */
  optimizeMemory(): void {
    memoryOptimizer.optimize();
  }

  /**
   * Dispose of all resources
   */
  dispose(): void {
    webglAccelerator.dispose();
    memoryOptimizer.stopMonitoring();
    this.isWebGLInitialized = false;
  }
}

// Global performance manager instance
export const performanceColorTransforms = new PerformanceColorTransforms();

/**
 * Convenience functions for backward compatibility
 */
export function applyColorTransform(cmyk: CMYK, transform: ColorTransformMatrix): CMYK {
  return performanceColorTransforms.applyColorTransform(cmyk, transform);
}

export function batchTransform(colors: CMYK[], transform: ColorTransformMatrix): CMYK[] {
  return performanceColorTransforms.batchTransform(colors, transform);
}
